import React from "react";
import { Trans, useTranslation } from "react-i18next";
import ExclusivelyCraftedImage from "../home/ExclusivelyCraftedImage";

/**
 * The definition beat of /preview/home-v2 — the homepage's answer to "what is
 * Agapaé?", in Agapaé's own voice.
 *
 * It replaces `ExclusivelyCraftedText` in the same slot and reuses
 * `ExclusivelyCraftedImage` unchanged, so the section is visually identical to
 * the one it stands in for: same `.im-frame` / `.p-wrap` composition, same
 * hairlines, same `data-w-id` scroll reveal, same `pad-20` / `heading-50` /
 * `body-20` type. Three deliberate differences, all of them the point:
 *
 *  1. **The heading is an `<h2>`, not an `<h3>`.** The live homepage opens
 *     `<h1>` → `<h3>` with no `<h2>` in between, which is the only real
 *     heading-hierarchy defect on the page.
 *  2. **A definition paragraph exists at all.** The live section says
 *     "Everything your celebration needs" — beautiful, and it names nothing.
 *     A reader (or a model) who leaves the homepage cannot say what Agapaé
 *     actually does. `definition.lead` says what Agapaé is and who it is
 *     for; `definition.scope` names what that covers, once, in prose rather
 *     than a feature list.
 *  3. **The live section's eyebrow is preserved verbatim.** Its old `<h3>`
 *     copy becomes this section's eyebrow, so the brand voice is added to
 *     rather than replaced. Its closing line is not carried over — the
 *     promise it made ("everything, in one place") is what the `<h2>` now
 *     says, and a section that opens and closes on the same sentence reads
 *     as one line printed twice.
 *
 * `id="features"` is kept: it is the target of the navbar's and footer's first
 * link, and moving it would break both the moment this page is promoted.
 */
const DefinitionSection = () => {
  const { t } = useTranslation();

  return (
    <section className="section topless">
      <div className="container">
        <ExclusivelyCraftedImage />
        <div id="features" className="p-wrap">
          <div className="vert-line _35vh mbm-exclusion"></div>
          <div className="p-block hv2-definition">
            <div
              data-w-id="44fdff4f-73c0-eaad-0d6f-6ca7c9797acb"
              style={{
                transform:
                  "translate3d(0, 70px, 0) scale3d(1, 1, 1) rotateX(-72deg) rotateY(23deg) rotateZ(0) skew(0, 13deg)",
                transformStyle: "preserve-3d",
                opacity: 0,
              }}
              className="pad-20"
            >
              <p className="hv2-eyebrow">{t("homeV2.definition.eyebrow")}</p>
              {/* The heading is two sentences — what Agapaé covers, and
                  where it lives — and where the break between them falls is a
                  typographic decision, not a consequence of the measure. A
                  locale carrying `<1/>` puts the second sentence on its own
                  line; one without it wraps naturally, which is the right
                  answer for English, where the first sentence is already wider
                  than the 620px measure at 50px. Same mechanism as the hero. */}
              <h2 className="heading-50">
                <Trans
                  i18nKey="homeV2.definition.heading"
                  components={{ 1: <br /> }}
                />
              </h2>
            </div>
            <div
              data-w-id="44fdff4f-73c0-eaad-0d6f-6ca7c9797ace"
              style={{
                transform:
                  "translate3d(0, 70px, 0) scale3d(1, 1, 1) rotateX(-72deg) rotateY(23deg) rotateZ(0) skew(0, 13deg)",
                transformStyle: "preserve-3d",
                opacity: 0,
              }}
            >
              {/* Two paragraphs, in the order the reader needs them: what
                  Agapaé IS, then what that covers. The definition stands
                  alone rather than opening the long enumerating sentence —
                  the one sentence a reader (or a model) has to leave with
                  should not need a comma-list read to the end before it
                  resolves. The promise itself is the heading above them, so
                  the section does not close by restating it. */}
              <p className="body-20 hv2-lead">{t("homeV2.definition.lead")}</p>
              <p className="body-20 hv2-scope">{t("homeV2.definition.scope")}</p>
            </div>
          </div>
          <div className="vert-line mbm-exclusion"></div>
        </div>
      </div>
    </section>
  );
};

export default DefinitionSection;
